import { pageFaqAdditions } from "@/data/pageFaqs";
import { PageFaqs } from "@/components/site/PageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { m } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  Check,
  Eye,
  Handshake,
  ShieldCheck,
} from "lucide-react";
import { canonicalLinks, jsonLd, ogUrl, ogImage, breadcrumbSchema, faqPageSchema, webPageSchema } from "@/lib/seo";
import { getTestimonial, relationshipLabel } from "@/data/testimonials";

const referralSignals = [
  "The founder is in every decision and the business stalls when they take a week off.",
  "Growth is outpacing structure: revenue is up, but so is the chaos.",
  "The numbers arrive late or messy, and you see the strain in the books before they do.",
  "Succession, sale or investment is on the horizon and the operations are not ready for diligence.",
  "The team keeps growing but nobody is clear on who owns what.",
  "A critical project or system change has been stuck for months.",
];

const faqs = pageFaqAdditions["/advisers"];

export const Route = createFileRoute("/advisers")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "For Advisers & Partners: Refer a Client | Ajility" },
      {
        name: "description",
        content:
          "For accountants, brokers and advisers with founder-led clients outgrowing their structure. We supply operational leadership; you stay the adviser.",
      },
      { property: "og:title", content: "For Advisers & Partners: Refer a Client | Ajility" },
      {
        property: "og:description",
        content:
          "For accountants, brokers and advisers with founder-led clients outgrowing their structure. We supply operational leadership; you stay the adviser.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "For Advisers & Partners: Refer a Client | Ajility" },
      { name: "twitter:description", content: "For accountants, brokers and advisers with founder-led clients outgrowing their structure. We supply operational leadership; you stay the adviser." },
      ...ogImage("advisers", "Ajility for advisers and referral partners"),
      ogUrl("/advisers"),
    ],
    links: canonicalLinks("/advisers"),
    scripts: [
      jsonLd(faqPageSchema("/advisers", faqs)),
      jsonLd(breadcrumbSchema([{ name: "For Advisers", path: "/advisers" }])),jsonLd(
        webPageSchema({ image: "advisers",
          path: "/advisers",
          name: "For Advisers & Partners: Refer a Client | Ajility",
          description:
            "How accountants, brokers and advisers refer founder-led clients to Ajility for hands-on fractional COO support, while staying central to the relationship.",
        }),
      )],
  }),
  component: AdvisersPage,
});

function AdvisersPage() {
  return (
    <div className="bg-surface pb-24">
      {/* HERO */}
      <section className="relative overflow-hidden bg-midnight pb-20 pt-32 text-frost md:pb-28 md:pt-40">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-steel blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-navy blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <m.p
            initial={false}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-sm font-semibold uppercase tracking-widest text-steel-on-dark"
          >
            For advisers & partners
          </m.p>
          <m.h1
            initial={false}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-5 font-serif text-4xl leading-tight md:text-5xl lg:text-6xl"
          >
            You look after their books. We'll help them run the business.
          </m.h1>
          <m.p
            initial={false}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-frost/80 md:text-xl"
          >
            Most of our clients arrive through their accountant, broker or adviser. If you
            have a founder-led client whose growth is outrunning their structure, this
            page explains how a referral works and why your relationship stays exactly
            where it is: at the centre.
          </m.p>
          <m.div
            initial={false}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-10"
          >
            <a
              href="mailto:adam@ajility.com.au?subject=Client%20referral"
              className="inline-flex h-12 items-center gap-2 rounded-full bg-frost px-7 text-base font-semibold text-midnight transition hover:bg-white"
            >
              Talk to Ajility about a client
              <ArrowRight className="h-4 w-4" />
            </a>
          </m.div>
        </div>
      </section>

      {/* WHY ADVISERS REFER */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-steel">
              The referral case
            </p>
            <h2 className="mt-4 font-serif text-3xl text-midnight md:text-4xl">
              Why advisers refer Ajility
            </h2>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              {
                icon: ShieldCheck,
                t: "We stay in our lane",
                d: "No accounting, tax, legal or lending. Ever. We work on structure, people and execution inside the business, and we send everything financial straight back to you. Your patch is your patch.",
              },
              {
                icon: Briefcase,
                t: "Your client gets an operator",
                d: "Not a report and an invoice. Ajility works inside the business, in the leadership meetings, week after week. Your client gets cleaner operations, better numbers and less firefighting, which makes your job easier too.",
              },
              {
                icon: Eye,
                t: "You stay in the loop",
                d: "With the client's permission, you see the Operating Diagnostic findings and the roadmap, and you hear how it is tracking. No surprises in the file at year end.",
              },
            ].map((c, i) => (
              <m.div
                key={c.t}
                initial={false}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl border border-foreground/8 bg-white p-8 shadow-sm"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-midnight text-frost">
                  <c.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-serif text-xl text-midnight">{c.t}</h3>
                <p className="mt-3 leading-relaxed text-charcoal">{c.d}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN TO REFER */}
      <section className="border-y border-foreground/8 bg-white py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-steel">
                Signals
              </p>
              <h2 className="mt-4 font-serif text-3xl text-midnight md:text-4xl">
                When to think of us
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-charcoal">
                You usually see it before anyone else does. If a client is showing a few of
                these signs, an introduction is probably worth their time.
              </p>
              <p className="mt-6 text-charcoal">
                Not sure? Send them the free{" "}
                <Link to="/tools" className="font-semibold text-midnight underline underline-offset-4 hover:text-steel">
                  Operations Health Score
                </Link>
                . Let the results start the conversation.
              </p>
            </div>
            <div className="rounded-2xl bg-surface p-8 md:p-10">
              <ul className="space-y-5">
                {referralSignals.map((s, i) => (
                  <li key={i} className="flex gap-4">
                    <Check className="mt-1 h-5 w-5 shrink-0 text-steel" />
                    <span className="leading-relaxed text-charcoal">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW A REFERRAL WORKS */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-steel">
              Process
            </p>
            <h2 className="mt-4 font-serif text-3xl text-midnight md:text-4xl">
              How a referral works
            </h2>
          </div>
          <div className="mt-14 space-y-8">
            {[
              {
                n: "1",
                t: "Introduce us",
                d: "A short email is enough: who the client is, what you are seeing and whether they know you are reaching out. Ajility keeps you central to the relationship, and legal matters stay with you. An email introduction or a quick three-way call. Whatever is easiest for you and the client.",
              },
              {
                n: "2",
                t: "Free 30-minute call",
                d: "Ajility speaks with the founder, no cost and no obligation. If we are not the right fit, we say so and suggest who is.",
              },
              {
                n: "3",
                t: "Fixed-fee start, you stay informed",
                d: "If it makes sense, the client starts with a fixed-fee Operating Diagnostic. With their permission, you see the findings and the plan.",
              },
            ].map((s, i) => (
              <m.div
                key={s.n}
                initial={false}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex gap-6 rounded-2xl border border-foreground/8 bg-white p-6 md:p-8"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-midnight font-serif text-xl text-frost">
                  {s.n}
                </div>
                <div>
                  <h3 className="font-serif text-xl text-midnight">{s.t}</h3>
                  <p className="mt-2 leading-relaxed text-charcoal">{s.d}</p>
                </div>
              </m.div>
            ))}
          </div>
          <p className="mt-10 text-center text-charcoal">
            On referral arrangements: most advisers simply want their client looked
            after, and that is how we treat every introduction. If your firm prefers a
            formal referral or partnership arrangement, we are open to that
            conversation.
          </p>
        </div>
      </section>

      {/* QUOTES */}
      <section className="border-y border-foreground/8 bg-white py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 md:grid-cols-2 md:gap-12">
            {["healthcare-md-integrator", "legal-principal-solicitor"].map((id) => {
              const t = getTestimonial(id);
              return (
                <div key={t.id} className="rounded-2xl bg-surface p-8 md:p-10">
                  <p className="text-sm font-semibold uppercase tracking-widest text-charcoal">
                    {t.context ?? relationshipLabel[t.relationship]}
                  </p>
                  <blockquote className="mt-6 font-serif text-xl leading-relaxed text-midnight md:text-2xl">
                    "{t.quote}"
                  </blockquote>
                  <p className="mt-6 text-sm text-charcoal">{t.role}</p>
                  <p className="mt-1 text-sm text-charcoal">
                    {relationshipLabel[t.relationship]}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PageFaqs faqs={faqs} />

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-3xl text-midnight md:text-4xl">
            Have a client in mind?
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-charcoal">
            A short email is enough to start: who they are, what you're seeing, and
            whether they know you're reaching out. Ajility replies within one business day.
          </p>
          <div className="mt-10 flex flex-col items-start justify-center gap-4 sm:flex-row">
            <a
              href="mailto:adam@ajility.com.au?subject=Client%20referral"
              className="inline-flex items-center gap-2 rounded-full bg-midnight px-7 py-4 text-sm font-semibold text-frost transition hover:bg-midnight-deep"
            >
              Email Ajility
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <BookFitCall
              placement="advisers_page"
              buttonClassName="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-6 py-4 text-sm font-medium text-foreground transition hover:bg-midnight hover:text-frost"
              noteClassName="mt-2 text-sm font-medium text-charcoal/80 hover:text-midnight"
            >
              Book a fit call instead
            </BookFitCall>
          </div>
        </div>
      </section>
    </div>
  );
}
